import type {ReactElement} from 'react';
import React, {useContext} from 'react';
import {ModalDetails} from '@Components/modal/modal-context';
import {
  digitalSignageFlow,
  qrCodeFlow,
  setHashedId,
  setPosterName,
  setPosterPreviewUrl,
  setWebLinksData,
  shareToAppFlow,
  socialMediaFlow,
  webpageEmbedFlow,
  webpagePublishFlow,
} from '@Panels/poster-publishing-options-panel/poster-publishing-options-slice';
import {DisplayType, PublishingAnalyticsAction, ThumbNailType} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {PublishingOptionListItem} from '@Panels/poster-publishing-options-panel/components/publishing-option-list-item/publishing-option-list-item';
import {GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {USER_PREMIUM_LEVELS} from '@Utils/user.util';
import {noop} from '@Utils/general.util';
import {openDownloadModal} from '@Components/poster-editor/library/poster-editor-open-modals';
import {useHashedId, useWebLinks} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.hooks';
import type {PublishingOptions, PublishingOptionsDefaultListProps} from './publishing-options-default-list.types';
import {PublishingOptionsList} from './publishing-options-default-list.types';
import {useAppDispatch} from '@/hooks';
import styles from './publishing-options-default-list.module.scss';
import {showErrorOnPanel} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel-library';
import {getEventWizardURLWithDesignPrefill, isEventsFeatureEnabled} from '@Libraries/events.library';
import {MediaTypes} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.types';
import {NewFeatureTag} from '@Components/new-feature-tag';
import {openUpsellingDialogForEmailCampaigns} from '@Libraries/email-campaigns-library';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Tag, TagType} from '@Components/tag';
import {getPosterThumbnailPreviewURL} from '@Libraries/poster-library';

export function PublishingOptionsDefaultList({...props}: PublishingOptionsDefaultListProps): ReactElement<any> {
  const modalDetails = useContext(ModalDetails);
  const inMobile = modalDetails?.isMobileModal;
  const {displayType} = props;
  const webLinks = useWebLinks();
  const dispatch = useAppDispatch();
  const hashedId = useHashedId();

  const handleDownloadClick = (): void => {
    if (hashedId) {
      window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.PUBLISH, {[GA4EventParamName.TYPE]: 'Download'});
      openDownloadModal(undefined, '', hashedId);
      props.closePanel();
    }
  };

  const startShareToAppFlow = (): void => {
    window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.PUBLISH, {[GA4EventParamName.TYPE]: 'ShareToApp'});
    dispatch(shareToAppFlow());
  };
  const startDigitalSignageFlow = (): void => {
    window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.PUBLISH, {[GA4EventParamName.TYPE]: 'DigitalSignage'});
    dispatch(digitalSignageFlow());
  };

  const startWebpagePublishFlow = (): void => {
    window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.PUBLISH, {[GA4EventParamName.TYPE]: 'ShareHostedLink'});
    dispatch(webpagePublishFlow());
  };

  const startWebpageEmbedFlow = (): void => {
    window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.PUBLISH, {[GA4EventParamName.TYPE]: 'Embed'});
    dispatch(webpageEmbedFlow());
  };
  const startSocialMediaFlow = (): void => {
    window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.PUBLISH, {[GA4EventParamName.TYPE]: 'SocialLink'});
    dispatch(socialMediaFlow());
  };

  const handleSocialMediaClick = (): void => {
    if (hashedId) {
      window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.PUBLISH, {[GA4EventParamName.TYPE]: 'SocialMediaPublishing'});
      window.location.href = window.PMW.util.site_url(`socialmedia/post/design/${hashedId}`);
    }
  };

  const handleEventCreationClick = (): void => {
    if (!hashedId) {
      return;
    }
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_CREATE_CLICK);
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_CREATED_FROM_EXT_SRC, {[GA4EventParamName.SOURCE]: 'My designs'});
    window.location.href = getEventWizardURLWithDesignPrefill(hashedId, MediaTypes.MY_DESIGNS);
  };
  const handleEmailCampaignClick = (): void => {
    if (hashedId) {
      window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.PUBLISH, {[GA4EventParamName.TYPE]: 'EmailCampaign'});
      if (window.PMW.getUserPremiumLevel() === USER_PREMIUM_LEVELS.PAYG) {
        window.PMW.closeModalDialog('download-publish-dialog');
        openUpsellingDialogForEmailCampaigns();
      } else {
        window.location.href = window.PMW.util.site_url(`emailmarketing/publish/design/${hashedId}`);
      }
    }
  };

  const handleQRCodeClick = (): void => {
    window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.PUBLISH, {[GA4EventParamName.TYPE]: 'QRcode'});
    dispatch(qrCodeFlow());
    if (!webLinks.hasUserSeenQrOption) {
      void window.PMW.writeLocal('user/markUserHasSeenPublishingQrOption');
      dispatch(setWebLinksData({hasUserSeenQrOption: true}));
    }
  };

  const getAfterHeadingTagForQrOption = (): ReactElement<any> | undefined => {
    if (webLinks.isQrHostedLinkPublished) {
      return <Tag text={window.i18next.t('pmwjs_live')} type={TagType.SECONDARY_LIGHT} className="radius-round spacing-m-l-2" showNewPulse/>;
    }
    if (!webLinks.hasUserSeenQrOption) {
      return <NewFeatureTag className="radius-round spacing-m-l-2" />;
    }
    return ;
  }

  const getPanelStyles = (): string => {
    if (inMobile) {
      return `flex-v-row _overflow-auto spacing-p-t-1 spacing-p-b-4 spacing-p-l-4 spacing-p-r-4 ${styles.publishingOptionsGapDropDown}`;
    } else {
      return `flex-v-row _overflow-auto ${displayType === DisplayType.MODAL ? `spacing-p-t-6 spacing-p-b-6 spacing-p-l-12 spacing-p-r-12  ${styles.publishingOptionsGapPanel}` : `spacing-p-3 ${styles.publishingOptionsGapDropDown}`}`;
    }
  };

  const getPublishingOptionsById = (id: PublishingOptionsList): PublishingOptions => {
    switch (id) {
      case PublishingOptionsList.SOCIAL_MEDIA:
        return {
          id: PublishingOptionsList.SOCIAL_MEDIA,
          iconName: 'icon-social-media',
          heading: window.i18next.t('pmwjs_social_media'),
          subheading: window.i18next.t('pmwjs_social_media_desc'),
          thumbNailType: ThumbNailType.ICON,
          onClick: handleSocialMediaClick,
        };
      case PublishingOptionsList.DIGITAL_SIGNAGE:
        return {
          id: PublishingOptionsList.DIGITAL_SIGNAGE,
          iconName: 'icon-tv',
          heading: window.i18next.t('pmwjs_digital_signage_new'),
          subheading: window.i18next.t('pmwjs_digital_signage_desc'),
          thumbNailType: ThumbNailType.ICON,
          onClick: startDigitalSignageFlow,
        };
      case PublishingOptionsList.WEB_LINK:
        return {
          id: PublishingOptionsList.WEB_LINK,
          iconName: 'icon-link',
          heading: window.i18next.t('pmwjs_publish_weblink'),
          subheading: window.i18next.t('pmwjs_publish_weblink_desc'),
          thumbNailType: ThumbNailType.ICON,
          onClick: startWebpagePublishFlow,
          afterHeadingTextContent: webLinks.isPublishedLink ? <Tag text={window.i18next.t('pmwjs_live')} type={TagType.SECONDARY_LIGHT} className="radius-round spacing-m-l-2" showNewPulse/> : undefined,
        };
      case PublishingOptionsList.EMAIL_CAMPAIGN:
        return {
          id: PublishingOptionsList.EMAIL_CAMPAIGN,
          iconName: 'icon-envelope',
          heading: window.i18next.t('pmwjs_email_campaign_new'),
          subheading: window.i18next.t('pmwjs_email_campaign_desc'),
          thumbNailType: ThumbNailType.ICON,
          onClick: handleEmailCampaignClick,
        };
      case PublishingOptionsList.DOWNLOADED_POSTER:
        return {
          id: PublishingOptionsList.DOWNLOADED_POSTER,
          iconName: 'icon-download',
          heading: window.i18next.t('pmwjs_download'),
          subheading: window.i18next.t('pmwjs_download_desc'),
          thumbNailType: ThumbNailType.ICON,
          onClick: handleDownloadClick,
        };
      case PublishingOptionsList.EMBED_IN_WEBPAGE:
        return {
          id: PublishingOptionsList.EMBED_IN_WEBPAGE,
          iconName: 'icon-code',
          heading: window.i18next.t('pmwjs_embed_in_webpage'),
          subheading: window.i18next.t('pmwjs_embed_in_webpage_desc'),
          thumbNailType: ThumbNailType.ICON,
          onClick: startWebpageEmbedFlow,
        };
      case PublishingOptionsList.SHARE_ON_SOCIAL_MEDIA:
        return {
          id: PublishingOptionsList.SHARE_ON_SOCIAL_MEDIA,
          iconName: 'icon-share-external',
          heading: window.i18next.t('pmwjs_share_link_social'),
          subheading: window.i18next.t('pmwjs_link_on_social_media_desc'),
          thumbNailType: ThumbNailType.ICON,
          onClick: startSocialMediaFlow,
        };
      case PublishingOptionsList.SHARE_TO_APP:
        return {
          id: PublishingOptionsList.SHARE_TO_APP,
          iconName: 'icon-share-external',
          heading: window.i18next.t('pmwjs_share_to_app'),
          subheading: window.i18next.t('pmwjs_share_to_app_desc'),
          thumbNailType: ThumbNailType.ICON,
          onClick: startShareToAppFlow,
        };
      case PublishingOptionsList.QR_CODE:
        return {
          id: PublishingOptionsList.QR_CODE,
          iconName: 'icon-qr-content',
          heading: window.i18next.t('pmwjs_qrcode'),
          subheading: window.i18next.t('pmwjs_qrcode_desc'),
          thumbNailType: ThumbNailType.ICON,
          onClick: handleQRCodeClick,
          afterHeadingTextContent: getAfterHeadingTagForQrOption(),
        };
      case PublishingOptionsList.PROMOTE_AS_EVENT:
        return {
          id: PublishingOptionsList.PROMOTE_AS_EVENT,
          iconName: 'icon-event',
          heading: window.i18next.t('pmwjs_create_event_webpage'),
          subheading: window.i18next.t('pmwjs_create_event_webpage_from_your_design'),
          thumbNailType: ThumbNailType.ICON,
          onClick: handleEventCreationClick,
          afterHeadingTextContent: <NewFeatureTag className="radius-round spacing-m-l-2" />,
        };
    }
  };

  const onSaveSuccess = async (onClickFunction: VoidFunction): Promise<void> => {
    dispatch(setPosterName(window.posterEditor?.whiteboard?.name ?? ''));
    dispatch(setHashedId(window.posterEditor?.whiteboard?.hashedID ?? ''));

    const posterPreviewUrl = await getPosterThumbnailPreviewURL(window.posterEditor?.whiteboard?.hashedID ?? '');
    dispatch(setPosterPreviewUrl(posterPreviewUrl ?? ''));

    if (onClickFunction) {
      onClickFunction();
    }
  };

  const onOptionClick = (onClickFunction: VoidFunction): void => {
    if (window.PMW.isReactPosterMaker()) {
      window.posterEditor?.whiteboard?.savePoster
        .save()
        .then(() => {
          void onSaveSuccess(onClickFunction);
        })
        .catch(() => {
          showErrorOnPanel(window.i18next.t('pmwjs_error_message'));
        });
    } else {
      onClickFunction();
    }
  };

  const getRecommendedSection = (): ReactElement[] | null => {
    if (!props.recommendedOptions?.length) {
      return null;
    }
    return getPublishingOptionsList(props.recommendedOptions);
  };

  const getRecommendedSectionTitle = (): ReactElement | null => {
    if (!props.recommendedOptions?.length) {
      return null;
    }
    return (
      <div>
        <Text val={window.i18next.t('pmwjs_recommended')} className={'content-ai'} size={TextSize.XSMALL} bold={true} />
        <Icon icon={'icon-ai'} className={'content-ai spacing-p-l-1'} size={IconSize.SIZE_ICON_16} />
      </div>
    );
  };

  const getDefaultSectionTitle = (): ReactElement | null => {
    if (!props.recommendedOptions?.length) {
      return null;
    }
    return <Text val={window.i18next.t('pmwjs_more')} className={'content-body'} size={TextSize.XSMALL} bold={true} />;
  };

  const getDefaultPublishingOptionsIds = (): PublishingOptionsList[] => {
    const options = [PublishingOptionsList.SOCIAL_MEDIA];
    if (isEventsFeatureEnabled()) {
      options.push(PublishingOptionsList.PROMOTE_AS_EVENT);
    }
    if (inMobile) {
      options.push(PublishingOptionsList.SHARE_TO_APP);
    }
    options.push(
      ...[
        PublishingOptionsList.EMAIL_CAMPAIGN,
        PublishingOptionsList.DOWNLOADED_POSTER,
        PublishingOptionsList.QR_CODE,
        PublishingOptionsList.EMBED_IN_WEBPAGE,
        PublishingOptionsList.WEB_LINK,
        PublishingOptionsList.DIGITAL_SIGNAGE,
        PublishingOptionsList.SHARE_ON_SOCIAL_MEDIA,
      ]
    );
    return options.filter((option) => {
      return !props.recommendedOptions?.includes(option);
    });
  };

  const getPublishingOptionsList = (optionsIds: PublishingOptionsList[]): ReactElement[] => {
    return optionsIds.map((id: PublishingOptionsList) => {
      const option = getPublishingOptionsById(id);
      return (
        <PublishingOptionListItem
          key={option.id}
          customThumbnailClasses="bg-primary-10 content-primary"
          iconName={option.iconName}
          heading={option.heading}
          afterHeadingTextContent={option.afterHeadingTextContent}
          subheading={option.subheading}
          thumbNailType={ThumbNailType.ICON}
          onClick={() => {
            onOptionClick(option.onClick ?? noop);
          }}
        />
      );
    });
  };

  return (
    <div className={getPanelStyles()}>
      {getRecommendedSectionTitle()}
      {getRecommendedSection()}
      {getDefaultSectionTitle()}
      {getPublishingOptionsList(getDefaultPublishingOptionsIds())}
    </div>
  );
}
